/**
 * Hook for extracting and caching description items.
 * 
 * Uses on-demand AI extraction with local caching to avoid repeated API calls.
 * Cache is keyed by stepId and persists for the session.
 */

import { useRef, useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { EXTRACT_DESCRIPTION_ITEMS } from '../graphql/queries';

// ============================================================================
// Types
// ============================================================================

interface DescriptionExtractionResult {
  hasItems: boolean;
  items: string[];
}

interface UseDescriptionItemsResult {
  /** The extracted items (empty if not a list or not yet loaded) */
  items: string[];
  /** Whether the description contains list items */
  hasItems: boolean;
  /** Whether extraction is in progress */
  loading: boolean;
  /** Whether extraction has completed (cached or fresh) */
  ready: boolean;
}

// ============================================================================
// Global Cache
// ============================================================================

// Session-level cache keyed by stepId
// This persists across component mounts within the same session
const extractionCache = new Map<string, DescriptionExtractionResult>();

// ============================================================================
// Hook
// ============================================================================

/**
 * Extract and cache description items for a step.
 * 
 * - First call for a stepId triggers AI extraction
 * - Subsequent calls return cached result immediately
 * - Cache persists for the browser session
 * 
 * @param stepId - Unique step identifier (used as cache key)
 * @param description - The step description to extract items from
 * @returns Extraction result with items, loading state, and ready flag
 */
export function useDescriptionItems(
  stepId: string,
  description: string | undefined | null
): UseDescriptionItemsResult {
  const [result, setResult] = useState<DescriptionExtractionResult>({
    hasItems: false,
    items: [],
  });
  const [ready, setReady] = useState(false);
  
  // Track if we've initiated extraction for this stepId
  const initiatedRef = useRef<string | null>(null);

  const [extractItems, { loading }] = useLazyQuery(EXTRACT_DESCRIPTION_ITEMS, {
    fetchPolicy: 'no-cache', // We manage caching ourselves
    onCompleted: (data) => {
      if (data?.extractDescriptionItems) {
        const extraction: DescriptionExtractionResult = {
          hasItems: data.extractDescriptionItems.hasItems,
          items: data.extractDescriptionItems.items || [],
        };
        // Cache the result
        extractionCache.set(stepId, extraction);
        setResult(extraction);
        setReady(true);
      }
    },
    onError: (error) => {
      console.error('Description extraction failed:', error);
      // Cache empty result to avoid retrying
      const emptyResult: DescriptionExtractionResult = { hasItems: false, items: [] };
      extractionCache.set(stepId, emptyResult);
      setResult(emptyResult);
      setReady(true);
    },
  });

  // Effect to check cache or trigger extraction
  useEffect(() => {
    // Skip if no description or very short
    if (!description || description.trim().length < 10) {
      setResult({ hasItems: false, items: [] });
      setReady(true);
      return;
    }

    // Check cache first
    const cached = extractionCache.get(stepId);
    if (cached) {
      setResult(cached);
      setReady(true);
      return;
    }

    // Avoid duplicate requests for the same stepId
    if (initiatedRef.current === stepId) {
      return;
    }

    // Trigger extraction
    initiatedRef.current = stepId;
    extractItems({ variables: { description } });
  }, [stepId, description, extractItems]);

  return {
    items: result.items,
    hasItems: result.hasItems,
    loading,
    ready,
  };
}

/**
 * Clear the extraction cache.
 * Useful for testing or when regenerating steps.
 */
export function clearExtractionCache(): void {
  extractionCache.clear();
}

/**
 * Get a cached extraction result without triggering a new fetch.
 * Returns undefined if not cached.
 */
export function getCachedExtraction(stepId: string): DescriptionExtractionResult | undefined {
  return extractionCache.get(stepId);
}

export default useDescriptionItems;

